/**
 * El reducer de la pantalla de Informes. Puro; lo consume `InformeContext` con `useReducer`.
 *
 * Undo: sólo paneles (es lo único con "Deshacer" en el mockup), por el link del toast, acotado
 * a 20 pasos. Deshacer es un cambio más: no limpia los dirties.
 */
import {canonicalizeInforme, canonicalizeTemplate} from './canonical';
import {preferredChartDimension} from './chartFields';
import {applyClientChange} from './clientSwitch';
import {
    FIELD_KINDS,
    PANEL_TYPES,
    defaultPanel,
    defaultPanels,
    dimensionNames,
    emptyState,
    emptyTemplate,
    metricNames,
    templateStateFromLayout,
} from './informeModel';

export const MAX_UNDO = 20;

export const ACTIONS = Object.freeze({
    SET_CLIENT: 'SET_CLIENT',
    NEW_INFORME: 'NEW_INFORME',
    LOAD_INFORME: 'LOAD_INFORME',
    SET_NAME: 'SET_NAME',
    SET_MODE: 'SET_MODE',
    SET_SETTINGS: 'SET_SETTINGS',
    FIELDS_APPLY: 'FIELDS_APPLY',
    TABLE_COLUMNS_APPLY: 'TABLE_COLUMNS_APPLY',
    APPLY_TEMPLATE: 'APPLY_TEMPLATE',
    PANEL_ADD: 'PANEL_ADD',
    PANEL_REMOVE: 'PANEL_REMOVE',
    PANEL_MOVE: 'PANEL_MOVE',
    PANEL_UPDATE: 'PANEL_UPDATE',
    PANEL_RENAME: 'PANEL_RENAME',
    UNDO: 'UNDO',
    SAVE_INFORME_OK: 'SAVE_INFORME_OK',
    SAVE_TEMPLATE_OK: 'SAVE_TEMPLATE_OK',
    PUBLISH_OK: 'PUBLISH_OK',
    CLEAR_ACCOUNTS_RESET: 'CLEAR_ACCOUNTS_RESET',
    GA4_SPLIT_KEEP: 'GA4_SPLIT_KEEP',
});

/**
 * Las tablas en las que el usuario eligió [Dejar así] ante el aviso de GA4 en filas aparte
 * (`ga4Split.js`). Es estado de la PANTALLA, no del informe: no entra a los canónicos (no ensucia)
 * ni se persiste — el backend descarta las `options` que no conoce. Vale para el informe abierto y
 * se borra al cargar otro, al crear uno, al aplicar una plantilla o al cambiar de cliente: los
 * layouts que arma el backend traen ids fijos (`table`), y un `Set` de la página que sobrevivía a
 * esos cambios callaba el aviso en otro informe o en otro cliente (review C-armado-03).
 */
export function isGa4SplitKept(state, uid) {
    return !!(state && state.ga4SplitKept && state.ga4SplitKept.includes(uid));
}

function withoutGa4SplitKept(state) {
    if (!state || !('ga4SplitKept' in state)) return state;
    const {ga4SplitKept, ...rest} = state;
    return rest;
}

function pushUndo(stack, entry) {
    return [...stack, entry].slice(-MAX_UNDO);
}

let undoSequence = 0;
/** El id de una entrada de Deshacer: el toast lo lleva para deshacer ESA acción y no la última. */
export function nextUndoId() {
    undoSequence += 1;
    return `u${undoSequence}`;
}

function withTemplateBaseline(state, template) {
    const next = {...state, template};
    return {...next, baseline: {...state.baseline, template: canonicalizeTemplate(next)}};
}

function withInformeBaseline(state) {
    return {...state, baseline: {...state.baseline, informe: canonicalizeInforme(state)}};
}

/**
 * La paleta más lo elegido que todavía no estaba: sostiene la invariante «paleta ⊇ columnas de cada
 * tabla» (de la que dependen los gráficos, que eligen sobre la paleta). Nunca quita nada.
 *
 * Lo nuevo entra al final DE SU TIPO, no al final de la lista: la paleta queda siempre «dimensiones,
 * después métricas», que es el orden en que la devuelve el modal de campos. Agregarlo al final la
 * intercalaba, y aplicar «Métricas y dimensiones» sin tocar nada la reordenaba y dejaba el informe
 * «Sin guardar» (`canonicalTemplate` compara el orden; review C-armado-01).
 */
function withPaletteFields(paletteFields, chosen) {
    const palette = paletteFields || [];
    const known = new Set(palette.map((f) => `${f.kind}:${f.name}`));
    const added = (chosen || []).filter((f) => !known.has(`${f.kind}:${f.name}`));
    if (!added.length) return palette;
    const all = [...palette, ...added];
    return [
        ...all.filter((f) => f.kind === FIELD_KINDS.DIMENSION),
        ...all.filter((f) => f.kind !== FIELD_KINDS.DIMENSION),
    ];
}

export function initialState(agencyClientId) {
    return emptyState(agencyClientId);
}

export function informeReducer(state = initialState(null), action) {
    switch (action.type) {
        case ACTIONS.SET_CLIENT:
            return withoutGa4SplitKept(applyClientChange(state, action.clientId, action.now).state);

        case ACTIONS.NEW_INFORME: {
            // Informe nuevo del cliente actual: sin plantilla, con los paneles del mockup si hay campos.
            const fields = action.fields || state.template.fields || [];
            // Sin campos, la hoja nace EN BLANCO: es la que deja `useSeedNewInforme` mientras llega
            // el perfil del cliente. Con `defaultPanels` nacerían gráficos sin métrica.
            const panels = fields.length ? defaultPanels(fields, action.fieldsData, action.kpis) : [];
            const template = {...emptyTemplate(), fields, panels};
            // Los DOS baselines se fijan sobre el RECIÉN NACIDO. El del informe, para que
            // `hasUnsavedWork` sepa que la pantalla en blanco no tiene nada que perder y no pregunte
            // al abrir otro informe; `isInformeDirty` no se entera —corta antes, por `informe:
            // null`— y el header sigue diciendo «Sin guardar», correcto para un informe nuevo
            // (card eo7J0qds). El de la hoja, para que `isSheetModified` pueda detectar los paneles
            // que el usuario agregue sobre un informe SIN plantilla (review C-informes-06);
            // `isTemplateModified` sigue en `false` porque exige `template.id`.
            const born = withTemplateBaseline(
                {...withoutGa4SplitKept(state), informe: null, baseline: {informe: null, template: null}, undoStack: []},
                template,
            );
            return withInformeBaseline(born);
        }

        case ACTIONS.LOAD_INFORME: {
            const {informe} = action;
            const template = templateStateFromLayout(informe.layout, {
                id: informe.source_template_id || null,
                name: informe.source_template_name || null,
            });
            const loaded = {
                ...withoutGa4SplitKept(state),
                agencyClientId: informe.agency_client_id,
                informe: {id: informe.id, name: informe.name, mode: informe.mode, is_published: !!informe.is_published,
                          description: informe.description || ''},
                settings: {...state.settings, ...(informe.settings || {})},
                template,
                undoStack: [],
                accountsResetAt: null,
            };
            const withTemplate = withTemplateBaseline(loaded, template);
            return withInformeBaseline(withTemplate);
        }

        case ACTIONS.SET_NAME:
            return {...state, informe: {...(state.informe || {}), name: action.name}};

        case ACTIONS.SET_MODE:
            return {...state, informe: {...(state.informe || {}), mode: action.mode}};

        case ACTIONS.SET_SETTINGS:
            return {...state, settings: {...state.settings, ...action.patch}};

        case ACTIONS.FIELDS_APPLY: {
            // Cambian las métricas y dimensiones: los paneles que apuntaban a algo que ya no está
            // caen al primer valor disponible (misma regla que los selectores del Explorador).
            //
            // La paleta es el UNIVERSO del informe, no las columnas de las tablas: quitar un
            // campo lo saca de las tablas que lo mostraban, pero agregar uno NO entra a ninguna
            // sola. Meterlo en todas era el bug de la card hwuk9PW7 — cada tabla se recorta
            // desde su propio «Editar» (`TABLE_COLUMNS_APPLY`).
            const fields = action.fields;
            const metrics = metricNames(fields);
            const dimensions = dimensionNames(fields);
            const panels = state.template.panels.map((panel) => {
                // Un bloque de texto no usa la paleta. Ojo: el backend le devuelve `metrics: []`,
                // y como `[]` es verdadero, la rama de abajo le ponía la primera métrica.
                if (panel.type === PANEL_TYPES.TEXT) return panel;
                const next = {...panel};
                if (next.metrics) {
                    const kept = next.metrics.filter((m) => metrics.includes(m));
                    next.metrics = kept.length ? kept : (metrics[0] ? [metrics[0]] : []);
                }
                if (next.type === PANEL_TYPES.TABLE && next.dimensions) {
                    // Una tabla SÍ puede quedarse sin dimensiones (es un total), así que acá no
                    // hay fallback al primer valor: sólo se poda lo que salió de la paleta.
                    next.dimensions = next.dimensions.filter((d) => dimensions.includes(d));
                }
                if (next.type === PANEL_TYPES.BARS || next.type === PANEL_TYPES.PIE) {
                    // La primera dimensión GRAFICABLE, no la última: con «Meta - Anuncios» la
                    // última es Imagen/video y el gráfico nacía con URLs (card PlFYrmTo).
                    if (!dimensions.includes(next.dimension)) {
                        next.dimension = preferredChartDimension(dimensions, action.fieldsData);
                    }
                }
                return next;
            });
            return {...state, template: {...state.template, fields, panels}};
        }

        case ACTIONS.TABLE_COLUMNS_APPLY: {
            // Las columnas de UNA tabla. Lo que se eligió y todavía no estaba en la paleta se
            // SUMA a la paleta, para que elegir una columna sea un solo gesto y se mantenga la
            // invariante «paleta ⊇ columnas de cada tabla» (de la que dependen los gráficos,
            // que eligen sobre la paleta). Nada se le quita a las otras tablas.
            const chosen = action.fields || [];
            const fields = withPaletteFields(state.template.fields, chosen);
            const panels = state.template.panels.map((panel) => (
                panel.uid === action.uid
                    ? {...panel, dimensions: dimensionNames(chosen), metrics: metricNames(chosen)}
                    : panel
            ));
            return {...state, template: {...state.template, fields, panels}};
        }

        case ACTIONS.APPLY_TEMPLATE: {
            // Aplicar = COPIA. La plantilla queda como procedencia y su baseline limpia.
            const template = templateStateFromLayout(action.template.layout, {
                id: action.template.id, name: action.template.name, is_shared: !!action.template.is_shared,
            });
            return withTemplateBaseline({...withoutGa4SplitKept(state), undoStack: []}, template);
        }

        case ACTIONS.PANEL_ADD: {
            const ctx = {metrics: metricNames(state.template.fields), dimensions: dimensionNames(state.template.fields),
                         fieldsData: action.fieldsData};
            const panel = action.panel || defaultPanel(action.panelType, ctx);
            const panels = [...state.template.panels];
            const at = Number.isInteger(action.at) ? Math.max(0, Math.min(action.at, panels.length)) : panels.length;
            panels.splice(at, 0, panel);
            // Un panel que nace armado (un sugerido, `presets.js`) trae sus columnas: entran a la
            // paleta como en `TABLE_COLUMNS_APPLY`, o la tabla mostraría campos que la hoja no tiene.
            const fields = action.fields ? withPaletteFields(state.template.fields, action.fields) : state.template.fields;
            return {...state, template: {...state.template, fields, panels}};
        }

        case ACTIONS.PANEL_REMOVE: {
            const index = state.template.panels.findIndex((p) => p.uid === action.uid);
            if (index < 0) return state;
            const panel = state.template.panels[index];
            const next = state.template.panels[index + 1];
            const panels = state.template.panels.filter((_, i) => i !== index);
            return {
                ...state,
                template: {...state.template, panels},
                // `beforeUid`: el vecino que lo seguía. Restituir por vecino y no por índice sobrevive
                // a un Agregar posterior (C-informes-43); el índice queda de fallback.
                undoStack: pushUndo(state.undoStack, {
                    id: action.entryId || nextUndoId(), kind: 'PANEL_REMOVED', index, panel,
                    beforeUid: next ? next.uid : null,
                }),
            };
        }

        case ACTIONS.PANEL_MOVE: {
            const {from, to} = action;
            const panels = [...state.template.panels];
            if (from === to || from < 0 || to < 0 || from >= panels.length || to >= panels.length) return state;
            const [moved] = panels.splice(from, 1);
            panels.splice(to, 0, moved);
            // Mover no entra al stack: si entrara, el «Deshacer» del toast de quitar desharía el
            // arrastre siguiente y el panel seguiría borrado (C-informes-17).
            return {...state, template: {...state.template, panels}};
        }

        case ACTIONS.PANEL_UPDATE: {
            const panels = state.template.panels.map((p) => (p.uid === action.uid ? {...p, ...action.patch} : p));
            return {...state, template: {...state.template, panels}};
        }

        case ACTIONS.PANEL_RENAME: {
            const title = (action.title || '').replace(/\s+/g, ' ').trim();
            const panels = state.template.panels.map((p) => (p.uid === action.uid ? {...p, title} : p));
            return {...state, template: {...state.template, panels}};
        }

        case ACTIONS.UNDO: {
            if (!state.undoStack.length) return state;
            const entry = state.undoStack[state.undoStack.length - 1];
            // Un toast viejo no deshace otra cosa: si su entrada ya no es la última, no hace nada.
            if (action.entryId && entry.id !== action.entryId) return state;
            const undoStack = state.undoStack.slice(0, -1);
            const panels = [...state.template.panels];
            if (entry.kind === 'PANEL_REMOVED') {
                if (panels.some((p) => p.uid === entry.panel.uid)) return {...state, undoStack};
                const byNeighbour = entry.beforeUid ? panels.findIndex((p) => p.uid === entry.beforeUid) : -1;
                const at = byNeighbour >= 0 ? byNeighbour : Math.min(entry.index, panels.length);
                panels.splice(at, 0, entry.panel);
            }
            return {...state, template: {...state.template, panels}, undoStack};
        }

        case ACTIONS.SAVE_INFORME_OK: {
            // Sólo el baseline del informe: guardar el informe no limpia la plantilla. Y el baseline
            // es el de lo que VIAJÓ (`sent`), no el del estado actual: una edición hecha mientras el
            // request estaba en vuelo sigue sucia (C-informes-16).
            const informe = {...(state.informe || {}), ...(action.informe || {})};
            const next = {...state, informe};
            const sent = action.sent || {};
            const asSent = {...next, settings: sent.settings || next.settings, template: sent.template || next.template};
            return {...next, baseline: {...next.baseline, informe: canonicalizeInforme(asSent)}};
        }

        case ACTIONS.PUBLISH_OK: {
            // Publicar no guarda: sólo `is_published`, y el baseline no se toca (C-informes-16).
            if (!state.informe) return state;
            return {...state, informe: {...state.informe, is_published: !!action.isPublished}};
        }

        case ACTIONS.SAVE_TEMPLATE_OK: {
            // Sólo el baseline de la plantilla: guardar la plantilla no limpia el informe.
            const template = {
                ...state.template,
                id: action.template.id,
                name: action.template.name,
                is_shared: !!action.template.is_shared,
            };
            return withTemplateBaseline(state, template);
        }

        case ACTIONS.CLEAR_ACCOUNTS_RESET:
            return {...state, accountsResetAt: null};

        case ACTIONS.GA4_SPLIT_KEEP:
            if (!action.uid || isGa4SplitKept(state, action.uid)) return state;
            return {...state, ga4SplitKept: [...(state.ga4SplitKept || []), action.uid]};

        default:
            return state;
    }
}
